<template>
  <Teleport to="#modal">
    <div
      class="modal d-block forum-overlay"
      tabindex="1"
      role="dialog"
      ref="forumModal"
      :style="{ '--overlay-z': forumZ }"
      :class="{ 'inga-modal-overlay-dark': isDarkMode }"
      @click.self="close"
      @keyup.esc="onEsc"
    >
      <div class="modal-dialog modal-dialog-centered forum-dialog">
        <div
          class="modal-content border-0 forum-panel"
          :class="{ 'inga-modal-panel-dark': isDarkMode, 'forum-panel-dark': isDarkMode }"
        >
          <div class="forum-header d-flex justify-content-between align-items-start gap-3">
            <div class="d-flex align-items-center gap-3 min-w-0">
              <div
                v-if="topic.kind === 'isin'"
                class="forum-topic-mark"
                :class="{ 'forum-topic-fallback': !showShareLogo, 'forum-topic-mark-dark': isDarkMode }"
              >
                <img
                  v-if="showShareLogo"
                  :src="shareLogoUrl(topic.isin)"
                  :alt="topic.name"
                  @error="onLogoError"
                >
                <span v-else>{{ shareMonogram(topic.name) }}</span>
              </div>
              <img
                v-else-if="topicMarkSrc"
                :src="topicMarkSrc"
                class="forum-topic-photo"
                :alt="topic.name"
                role="button"
                @click.stop="openTopicPhoto"
              >
              <div class="min-w-0">
                <p
                  class="forum-kicker mb-0"
                  :class="kickerClass"
                >Fórum · {{ kindLabel }}</p>
                <div class="d-flex align-items-center gap-2 min-w-0">
                  <h4 class="forum-title mb-0 text-truncate">{{ topic.name || forum.title || 'Diskuze' }}</h4>
                  <button
                    v-if="forum.context?.ISINID"
                    type="button"
                    class="forum-chart-btn"
                    :class="{ 'forum-chart-btn-dark': isDarkMode }"
                    :title="'Graf: ' + (topic.name || 'akcie')"
                    @click.stop="openChart"
                  >
                    <fa-icon icon="fa-solid fa-chart-line" />
                  </button>
                </div>
                <span
                  v-if="topic.isin"
                  class="forum-isin-code font-monospace"
                >{{ topic.isin }}</span>
                <div
                  v-if="topic.kind === 'isin' && topic.price != null && topic.price !== ''"
                  class="forum-quote"
                >
                  <span class="forum-quote-price">{{ formattedPrice }} Kč</span>
                  <span class="forum-quote-change" :class="priceClass">
                    {{ formatChangeTypeNumber(topic.prcDf) }}
                    ({{ formatChangeTypeNumber(topic.pctDf) }}%)
                  </span>
                  <span class="forum-quote-spark" aria-hidden="true">
                    <svg v-if="Number(topic.prcDfType) === 1" width="44" height="16" viewBox="0 0 50 20" fill="none">
                      <path d="M2 16C10 15 15 10 23 9C31 8 38 5 48 3" stroke="#15803D" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <svg v-else-if="Number(topic.prcDfType) === -1" width="44" height="16" viewBox="0 0 50 20" fill="none">
                      <path d="M2 4 C10 5 15 10 23 11C31 12 38 15 48 17" stroke="#B91C1C" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <svg v-else width="44" height="16" viewBox="0 0 50 20" fill="none">
                      <path d="M2 10 C10 12 18 6 25 10C32 14 40 8 48 10" :stroke="isDarkMode ? '#64748B' : '#CBD5E1'" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </span>
                </div>
              </div>
            </div>
            <button
              type="button"
              class="forum-close"
              :class="{ 'forum-close-dark': isDarkMode }"
              aria-label="Zavřít"
              @click="close"
            >&times;</button>
          </div>

          <div class="forum-body" :class="{ 'forum-body-dark': isDarkMode }">
            <div v-if="forum.loading" class="forum-empty">
              <div class="spinner-border spinner-border-sm text-warning mb-2" role="status"></div>
              <span>Načítám příspěvky…</span>
            </div>
            <div v-else-if="!posts.length" class="forum-empty">
              <fa-icon icon="fa-regular fa-comments" class="forum-empty-icon" />
              <span class="d-block fw-medium">Zatím tu nikdo nic nenapsal.</span>
              <span class="text-xs">Buď první, kdo otevře diskuzi.</span>
            </div>
            <Timeline v-else :value="posts" layout="vertical" align="left" class="forum-timeline">
              <template #marker="slotProps">
                <img
                  class="forum-avatar"
                  :class="{ 'forum-avatar-link': userId(slotProps.item) }"
                  :src="avatarUrl(slotProps.item)"
                  :alt="nick(slotProps.item)"
                  :title="userId(slotProps.item) ? 'Profil hráče' : undefined"
                  @click.stop="goToUser(slotProps.item)"
                >
              </template>
              <template #opposite>
                <span></span>
              </template>
              <template #content="slotProps">
                <article
                  class="forum-post"
                  :class="{
                    'forum-post-inga': isInga(slotProps.item),
                    'forum-post-mention': canMention(slotProps.item),
                  }"
                  @click="mentionAuthor(slotProps.item)"
                >
                  <div class="forum-post-meta">
                    <span class="forum-nick">{{ nick(slotProps.item) }}</span>
                    <span class="forum-date">{{ date(slotProps.item) }}</span>
                  </div>
                  <p class="forum-text mb-0">{{ text(slotProps.item) }}</p>
                </article>
              </template>
            </Timeline>
          </div>

          <div class="forum-composer" :class="{ 'forum-composer-dark': isDarkMode }">
            <template v-if="isLoggedIn">
              <textarea
                ref="composer"
                v-model="draft"
                class="form-control forum-input"
                rows="3"
                maxlength="2000"
                placeholder="Napiš příspěvek (alespoň 5 znaků)…"
                @keydown.ctrl.enter="submit"
              ></textarea>
              <div class="d-flex justify-content-between align-items-center mt-2 gap-2">
                <span class="forum-hint">{{ draftHint }}</span>
                <button
                  type="button"
                  class="forum-send-btn"
                  :disabled="!canSend || sending"
                  @click="submit"
                >
                  {{ sending ? 'Odesílám…' : 'Odeslat' }}
                </button>
              </div>
            </template>
            <div v-else class="forum-login-hint">
              <span>Příspěvek může přidat jen přihlášený hráč.</span>
              <button type="button" class="forum-login-btn" @click="openLogin">Přihlásit se</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import Timeline from 'primevue/timeline';
import swal from 'sweetalert2';
import dataService from '@/services/dataService.js';
import { shareLogoUrl, shareMonogram } from '@/helpers/shareLogos';
import { userAvatarUrl } from '@/helpers/userAvatars';
import { formatTrimZeroCents } from '@/utils/NumberFormat';

const store = useStore();
const router = useRouter();
const forumModal = ref(null);
const composer = ref(null);
const draft = ref('');
const sending = ref(false);
const logoFailed = ref(false);
const topic = ref({
  kind: null, name: '', isin: '', img: '',
  price: null, prcDf: null, pctDf: null, prcDfType: 0,
});

const isDarkMode = computed(() => !!store.state.isDarkMode);
const isLoggedIn = computed(() => !!store.getters.isLoggedIn);
const forum = computed(() => store.state.forum);
const forumZ = computed(() => Number(forum.value.zIndex) || 2250);
const posts = computed(() => forum.value.posts || []);
const imgRoot = computed(() => process.env.VUE_APP_IMG_ROOT);
const apiRoot = computed(() => process.env.VUE_APP_API_URL);

const kindLabel = computed(() => {
  if (topic.value.kind === 'isin') return 'Akcie';
  if (topic.value.kind === 'game') return 'Hra';
  if (topic.value.kind === 'reward') return 'Výhra';
  return 'Diskuze';
});

const kickerClass = computed(() => {
  if (topic.value.kind === 'isin') return 'kicker-isin';
  if (topic.value.kind === 'game') return 'kicker-game';
  if (topic.value.kind === 'reward') return 'kicker-reward';
  return '';
});

const showShareLogo = computed(() => !!(shareLogoUrl(topic.value.isin) && !logoFailed.value));

const topicMarkSrc = computed(() => {
  const file = topic.value.img;
  if (!file) return '';
  const value = String(file);
  if (value.startsWith('http')) return value;
  if (value.startsWith('img/')) return apiRoot.value + value;
  return imgRoot.value + value;
});

const onLogoError = () => {
  logoFailed.value = true;
};

const emptyTopic = () => ({
  kind: null, name: '', isin: '', img: '',
  price: null, prcDf: null, pctDf: null, prcDfType: 0,
});

const formattedPrice = computed(() => formatTrimZeroCents(topic.value.price) || '—');
const formatChangeTypeNumber = (value) => store.getters.formatChangeTypeNumber(value);
const priceClass = computed(() => ({
  'text-success': Number(topic.value.prcDfType) === 1,
  'text-info': Number(topic.value.prcDfType) === 0,
  'text-danger': Number(topic.value.prcDfType) === -1,
}));

const sameId = (a, b) => Number(a) === Number(b);

watch(
  () => [forum.value.isOpen, forum.value.context, forum.value.title],
  async () => {
    if (!forum.value.isOpen) {
      topic.value = emptyTopic();
      return;
    }
    const ctx = forum.value.context || {};
    const kind = ctx.ISINID ? 'isin' : (ctx.GameID ? 'game' : (ctx.RewardID ? 'reward' : null));
    logoFailed.value = false;
    const next = {
      ...emptyTopic(),
      kind,
      name: forum.value.title || '',
      isin: ctx.ISIN || '',
      img: ctx.Img || '',
    };
    if (kind === 'isin' && !next.isin) {
      const hit = (store.state.ISINStatusInfo || []).find((row) => sameId(row.ISINID, ctx.ISINID));
      if (hit?.ISIN) {
        next.isin = hit.ISIN;
        next.name = next.name || hit.ISINName;
        next.price = hit.ISINPriceListPrice;
        next.prcDf = hit.ISINPriceListAmtDiff;
        next.pctDf = hit.ISINPriceListPctDiff;
        next.prcDfType = hit.PriceChangeType ?? hit.PrcDfType ?? 0;
      }
    }
    topic.value = { ...next };
    if (kind === 'isin' && ctx.ISINID) {
      const data = await dataService.GetISINDetail(ctx.ISINID);
      if (!forum.value.isOpen) return;
      if (data) {
        next.isin = data.ISIN || next.isin;
        next.name = data.ISINName || next.name;
        next.price = data.ISINPrice;
        next.prcDf = data.PrcDf;
        next.pctDf = data.PctDf;
        next.prcDfType = data.PrcDfType;
        topic.value = { ...next };
      }
    }
    if (kind === 'reward' && ctx.RewardID && !next.img) {
      const data = await dataService.GetRewardDetail(ctx.RewardID);
      if (!forum.value.isOpen) return;
      if (data?.RewardImg) {
        next.img = data.RewardImg;
        next.name = data.RewardName || next.name;
        topic.value = { ...next };
      }
    }
    if (kind === 'game' && ctx.GameID && !next.img) {
      const open = (store.state.OpenGame || []).find((row) => sameId(row.GameID, ctx.GameID));
      if (open?.GameRewardImg) {
        next.img = open.GameRewardImg;
        next.name = open.GameName || next.name;
        topic.value = { ...next };
        return;
      }
      const data = await dataService.GetOpenGameDetail(ctx.GameID);
      if (!forum.value.isOpen) return;
      if (data?.RewardImg) next.img = data.RewardImg;
      if (data?.GameName) next.name = data.GameName;
      topic.value = { ...next };
    }
  },
  { immediate: true }
);

const canSend = computed(() => draft.value.trim().length >= 5);
const draftHint = computed(() => {
  const len = draft.value.trim().length;
  if (len === 0) return 'Ctrl+Enter odešle příspěvek';
  if (len < 5) return `Ještě ${5 - len} ${5 - len === 1 ? 'znak' : 'znaky'}`;
  return `${len} znaků`;
});

const field = (item, ...keys) => {
  if (!item) return null;
  for (const key of keys) {
    if (item[key] != null && item[key] !== '') return item[key];
  }
  return null;
};

const nick = (item) => field(item, 'UN', 'UserNickName', 'Nick') || 'INGA';
const date = (item) => field(item, 'Dt', 'SysDate', 'Date') || '';
const text = (item) => field(item, 'Tx', 'ChatText', 'Text') || '';
const userId = (item) => field(item, 'UI', 'UserID');
const isInga = (item) => Number(field(item, 'St', 'System')) === 1 || !userId(item);

const avatarUrl = (item) => {
  const cache = store.state.imageCacheKey;
  if (isInga(item)) {
    return `${imgRoot.value}inga.jpg?cache=${cache}`;
  }
  return userAvatarUrl(field(item, 'Ug', 'UserImage', 'Image'), cache, true);
};

const close = () => store.dispatch('closeForum');

const openTopicPhoto = () => {
  if (!topicMarkSrc.value) return;
  store.dispatch('openLightbox', {
    src: topicMarkSrc.value,
    alt: topic.value.name || 'Výhra'
  });
};

const onEsc = () => {
  store.dispatch("closeTopOverlay");
};

const onDocumentEsc = (event) => {
  if (event.key !== "Escape") return;
  if (!store.state.forum.isOpen && !store.state.isinChartModal?.isOpen) return;
  if (store.state.lightbox?.isOpen) return;
  event.preventDefault();
  event.stopPropagation();
  store.dispatch("closeTopOverlay");
};

const openChart = () => {
  const ctx = forum.value.context;
  if (!ctx?.ISINID) return;
  store.dispatch("fetchISINChartData", {
    ISINID: ctx.ISINID,
    ISIN: ctx.ISIN,
    Nm: forum.value.title
  });
};

onMounted(() => {
  forumModal.value?.focus();
  document.addEventListener('keydown', onDocumentEsc);
});

onUnmounted(() => {
  document.removeEventListener('keydown', onDocumentEsc);
});

watch(() => store.state.isinChartModal?.isOpen, (isOpen, wasOpen) => {
  if (wasOpen && !isOpen && store.state.forum.isOpen) {
    forumModal.value?.focus();
  }
});

watch(() => store.state.modal.isOpen, (isOpen, wasOpen) => {
  if (wasOpen && !isOpen && store.state.forum.isOpen) {
    forumModal.value?.focus();
  }
});

watch(isLoggedIn, (logged) => {
  if (logged && forum.value.isOpen) store.dispatch('loadForum');
});

const openLogin = () => {
  store.commit('toggleLoginForm');
};

const goToUser = (item) => {
  const id = userId(item);
  if (!id) return;
  close();
  router.push({ name: 'Detail her hráče', params: { uid: id } });
};

const myUserId = computed(() => store.getters.getUser?.UserID);

const isMyPost = (item) => {
  const mine = myUserId.value;
  const author = userId(item);
  if (mine == null || author == null || mine === '' || author === '') return false;
  return String(mine) === String(author);
};

const mentionPrefix = (item) => `Pro ${nick(item)}: `;

const isPlaceholderDraft = () => {
  const value = draft.value;
  if (!value) return true;
  return /^Pro .+?: $/.test(value);
};

const canMention = (item) => (
  isLoggedIn.value
  && isPlaceholderDraft()
  && !isMyPost(item)
  && !!nick(item)
);

const mentionAuthor = (item) => {
  if (!canMention(item)) return;
  draft.value = mentionPrefix(item);
  nextTick(() => {
    composer.value?.focus();
    const el = composer.value;
    if (el) {
      const end = el.value.length;
      el.setSelectionRange(end, end);
    }
  });
};

const setSending = (value) => {
  sending.value = value;
};

const clearDraft = () => {
  draft.value = '';
};

const submit = async () => {
  if (!canSend.value || sending.value) return;
  const text = draft.value;
  setSending(true);
  try {
    const response = await store.dispatch('sendForumPost', text);
    if (response?.Result) {
      clearDraft();
    } else {
      const messages = response?.ErrorMessage || ['Příspěvek se nepodařilo odeslat'];
      swal.fire({
        position: 'center',
        icon: 'error',
        html: `${(Array.isArray(messages) ? messages : [messages]).map((message) => `<h5>${message}</h5>`).join('')}`,
        showConfirmButton: false,
        timer: 4000,
      });
    }
  } finally {
    setSending(false);
  }
};
</script>

<style scoped>
.forum-overlay {
  z-index: var(--overlay-z, 2250) !important;
  background: rgba(15, 23, 42, 0.55);
}

.forum-dialog {
  max-width: 960px;
  width: 92%;
  margin: 1.5rem auto;
}

.forum-panel {
  display: flex;
  flex-direction: column;
  max-height: min(88vh, 760px);
  overflow: hidden;
  border-radius: 0.85rem !important;
}

.forum-header {
  padding: 1rem 1.15rem 0.75rem;
  flex-shrink: 0;
}

.forum-kicker {
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #FF8A4C;
}

.forum-kicker.kicker-isin {
  color: #0EA5E9;
}

.forum-kicker.kicker-reward {
  color: #D97706;
}

.forum-kicker.kicker-game {
  color: #FF6B21;
}

.forum-isin-code {
  display: block;
  margin-top: 2px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #64748B;
}

.forum-panel-dark .forum-isin-code {
  color: #94A3B8;
}

.forum-quote {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
}

.forum-quote-price {
  font-size: 0.92rem;
  font-weight: 800;
  color: #0F172A;
}

.forum-panel-dark .forum-quote-price {
  color: #E2E8F0;
}

.forum-quote-change {
  font-size: 0.78rem;
  font-weight: 700;
}

.forum-quote-spark {
  display: inline-flex;
  align-items: center;
  line-height: 0;
}

.forum-chart-btn {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1.5px solid #7DD3FC;
  background: transparent;
  color: #0EA5E9;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 0.52rem;
}

.forum-chart-btn:hover {
  color: #0284C7;
  border-color: #0EA5E9;
  background: rgba(14, 165, 233, 0.12);
}

.forum-chart-btn-dark {
  border-color: #38BDF8;
  color: #38BDF8;
}

.forum-chart-btn-dark:hover {
  color: #7DD3FC;
  border-color: #7DD3FC;
  background: rgba(56, 189, 248, 0.16);
}

.forum-topic-mark {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(14, 165, 233, 0.12);
}

.forum-topic-mark img {
  width: 44px;
  height: 44px;
  object-fit: contain;
}

.forum-topic-fallback {
  color: #0EA5E9;
  font-size: 0.78rem;
  font-weight: 800;
}

.forum-topic-mark-dark {
  background: rgba(56, 189, 248, 0.16);
}

.forum-topic-photo {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  cursor: zoom-in;
}

.forum-title {
  font-weight: 800;
  font-size: 1.15rem;
  color: #0F172A;
  min-width: 0;
}

.forum-panel-dark .forum-title {
  color: #E2E8F0;
}

.forum-close {
  background: none;
  border: none;
  color: #94A3B8;
  font-weight: 800;
  font-size: 1.75rem;
  line-height: 1;
  padding: 0 4px;
  cursor: pointer;
}

.forum-close:hover,
.forum-close-dark:hover {
  color: #FF8A4C;
}

.forum-body {
  flex: 1;
  overflow: auto;
  padding: 0.5rem 1.15rem 1rem;
  min-height: 220px;
}

.forum-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 4px;
  min-height: 220px;
  color: #64748B;
}

.forum-body-dark .forum-empty {
  color: #94A3B8;
}

.forum-empty-icon {
  font-size: 1.6rem;
  color: #FF8A4C;
  margin-bottom: 8px;
}

.forum-timeline {
  padding-top: 4px;
}

.forum-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px #E2E8F0;
  background: #F8FAFC;
}

.forum-panel-dark .forum-avatar {
  border-color: #334155;
  box-shadow: 0 0 0 1px #475569;
  background: #1E293B;
}

.forum-avatar-link {
  cursor: pointer;
}

.forum-post {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 0.75rem;
  padding: 0.7rem 0.85rem;
  margin: 0 0 12px 4px;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.forum-post:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.12);
}

.forum-post-mention {
  cursor: pointer;
}

.forum-panel-dark .forum-post {
  background: #1E293B;
  border-color: #475569;
}

.forum-panel-dark .forum-post:hover {
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.35);
}

.forum-post-inga {
  border-color: rgba(255, 138, 76, 0.45);
}

.forum-post-meta {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.forum-nick {
  font-weight: 800;
  font-size: 0.85rem;
  color: #0F172A;
}

.forum-panel-dark .forum-nick {
  color: #E2E8F0;
}

.forum-date {
  font-size: 0.72rem;
  color: #94A3B8;
  white-space: nowrap;
}

.forum-text {
  font-size: 0.88rem;
  line-height: 1.5;
  color: #334155;
  white-space: pre-wrap;
  word-break: break-word;
}

.forum-panel-dark .forum-text {
  color: #CBD5E1;
}

.forum-composer {
  flex-shrink: 0;
  padding: 0.85rem 1.15rem 1.1rem;
  border-top: 1px solid #E2E8F0;
  background: #fff;
}

.forum-composer-dark {
  border-top-color: #475569;
  background: #334155;
}

.forum-input {
  resize: none;
  font-size: 0.9rem;
}

.forum-hint {
  font-size: 0.72rem;
  color: #94A3B8;
}

.forum-send-btn,
.forum-login-btn {
  border: none;
  border-radius: 999px;
  padding: 0.4rem 1rem;
  font-size: 0.8rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: #FF8A4C;
  color: #fff;
  cursor: pointer;
}

.forum-send-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.forum-send-btn:not(:disabled):hover,
.forum-login-btn:hover {
  background: #ff6b21;
}

.forum-login-hint {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 0.85rem;
  color: #64748B;
}

.forum-composer-dark .forum-login-hint {
  color: #CBD5E1;
}

:deep(.p-timeline-event-opposite) {
  display: none !important;
  flex: 0 0 0 !important;
  width: 0 !important;
  padding: 0 !important;
}

:deep(.p-timeline-event-separator) {
  flex: 0 0 auto;
}

:deep(.p-timeline-event-connector) {
  background: #E2E8F0;
}

.forum-panel-dark :deep(.p-timeline-event-connector) {
  background: #475569;
}

:deep(.p-timeline-event-content) {
  flex: 1 1 auto;
  padding: 0 0 1.15rem 0.7rem;
}

:deep(.p-timeline-event-marker) {
  border: none;
  background: transparent;
}
</style>
